import React, { useEffect } from "react";
import { Button } from "@ui/button";
import { ListPlus, MoreHorizontal } from "lucide-react";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@ui/menubar";
import TranslatedText from "@components/items/display/TranslatedText";
import {
  moveToPlaylist,
  removeItemFromPlaylist,
  useAppDispatch,
  useAppSelector,
} from "@lib/stores";
import { useSearchParams } from "react-router-dom";
import { ContentActions } from "@lib/stores/content/slices";
import {
  getPlaylistByIdAsync,
  getPlaylistsAsync,
} from "@lib/stores/myLibrary/slices";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import { SVG } from "@components/items/common/SVG";

const PlaylistItemMenu = ({ itemId }: { itemId: string }) => {
  const dispatch = useAppDispatch();
  const playlists = useAppSelector((state) => state.myLibrary.playlists.data);
  const [searchParams] = useSearchParams();
  const currentPlaylistId = searchParams.get("id");

  useEffect(() => {
    dispatch(getPlaylistsAsync());
  }, [dispatch]);

  return (
    <Menubar className="border-none">
      <MenubarMenu>
        <MenubarTrigger
          asChild
          className="cursor-pointer rounded-none focus:bg-transparent focus-visible:ring-0 data-[state=open]:bg-transparent dark:text-white"
          onClick={(e) => e.stopPropagation()}
        >
          <Button variant="basic" className="gap-x-10">
            <MoreHorizontal size={25} />
          </Button>
        </MenubarTrigger>
        <MenubarContent align="start" className="border-none text-[#898A8D]">
          <MenubarSub>
            <MenubarSubTrigger
              className="flex items-center justify-around focus:bg-[#f8fafc] dark:text-white dark:focus:bg-[#0f172a]"
              onClick={(e) => e.stopPropagation()}
            >
              <ListPlus />
              <TranslatedText text={`my-library.${"move to another list"}`} />
            </MenubarSubTrigger>
            <MenubarSubContent className="ms-2 border-none">
              {playlists.map((p) => (
                <MenubarItem
                  key={p.id}
                  className="flex items-center gap-2 focus:bg-[#f8fafc] dark:focus:bg-[#0f172a]"
                  onClick={(e) => {
                    e.stopPropagation();
                    moveToPlaylist(
                      {
                        sourcePlaylistId: parseInt(currentPlaylistId ?? ""),
                        destinationPlaylistId: p.id,
                        contentEntityIds: [itemId],
                      },
                      () => {
                        dispatch(
                          ContentActions.getContentEntityItemsByPlaylistIdAsync(
                            parseInt(currentPlaylistId ?? "")
                          )
                        );
                      }
                    );
                  }}
                >
                  {p.picture ? (
                    <img
                      src={getPublicMediaUrl(p.picture)}
                      alt="No item"
                      className="h-10 w-10 rounded-lg"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#9999992B]/15">
                      <SVG src="/assets/icons/empty-playlist.svg" />
                    </div>
                  )}
                  {p.title}
                </MenubarItem>
              ))}
            </MenubarSubContent>
          </MenubarSub>
          <MenubarSeparator />
          <MenubarItem
            inset={true}
            className="text-red-500 focus:bg-[#f8fafc] focus:text-red-500 dark:focus:bg-[#0f172a]"
            onClick={(e) => {
              e.stopPropagation();
              removeItemFromPlaylist(
                {
                  playlistId: parseInt(currentPlaylistId ?? ""),
                  contentEntityId: itemId,
                },
                () => {
                  dispatch(
                    getPlaylistByIdAsync(parseInt(currentPlaylistId ?? ""))
                  );
                  dispatch(
                    ContentActions.getContentEntityItemsByPlaylistIdAsync(
                      parseInt(currentPlaylistId ?? "")
                    )
                  );
                }
              );
            }}
          >
            <TranslatedText text={`my-library.${"remove from list"}`} />
          </MenubarItem>
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
};

export default PlaylistItemMenu;
